// components/patient/PrescriptionUpload.jsx
import React, { useState } from 'react';
import { Card } from '../ui/Card';
import { Input } from '../ui/Input';
import { Button } from '../ui/Button';
import { Icon } from '../ui/Icon';
import { useBrand } from '../../contexts/BrandContext';
import { useToast } from '../../contexts/ToastContext';
import { DB } from '../../utils/db';

export function PrescriptionUpload({ user, onNavigate, onRefresh }) {
  const { brand } = useBrand();
  const toast = useToast();
  const [formData, setFormData] = useState({
    medication: '',
    prescriber: '',
    prescriberPhone: '',
    quantity: '',
    notes: ''
  });
  const [file, setFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  
  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };
  
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      
      // Create preview if image
      if (selectedFile.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setFilePreview(reader.result);
        };
        reader.readAsDataURL(selectedFile);
      } else {
        setFilePreview(null);
      }
    }
  };
  
  const handleSubmit = async () => {
    if (!formData.medication.trim()) {
      toast.addToast('Please enter medication name', 'error');
      return;
    }
    if (!formData.prescriber.trim()) {
      toast.addToast('Please enter prescriber name', 'error');
      return;
    }
    if (!formData.prescriberPhone.trim()) {
      toast.addToast('Please enter prescriber phone', 'error');
      return;
    }
    
    const rxId = `rx_${Date.now()}`;
    const prescription = {
      id: rxId,
      userId: user.id,
      medication: formData.medication.trim(),
      prescriber: formData.prescriber.trim(),
      prescriberPhone: formData.prescriberPhone.trim(),
      quantity: formData.quantity.trim(),
      notes: formData.notes.trim(),
      fileName: file?.name || null,
      hasFile: !!file,
      status: 'pending_review',
      uploadedAt: new Date().toISOString()
    };
    
    const prescriptions = await DB.get('d2p_prescriptions') || [];
    prescriptions.push(prescription);
    const saved = await DB.set('d2p_prescriptions', prescriptions);
    
    if (!saved) {
      toast.addToast('Failed to save prescription. Try clearing old data or browser storage.', 'error');
      return;
    }
    
    toast.addToast('Prescription uploaded successfully!');
    onRefresh && onRefresh();
    onNavigate('prescriptions');
  };
  
  return (
    <div style={{
      background: '#F9FAFB',
      minHeight: '100vh',
      padding: '40px 24px',
      fontFamily: 'Libre Franklin, sans-serif'
    }}>
      <div style={{ maxWidth: 800, margin: '0 auto' }}>
        <div style={{ marginBottom: 32 }}>
          <Button
            variant="ghost"
            icon="arrowLeft"
            onClick={() => onNavigate('dashboard')}
            style={{ marginBottom: 16 }}
          >
            Back to Dashboard
          </Button>
          <h1 style={{ fontSize: 32, fontWeight: 700, marginBottom: 8, color: brand.c.navy }}>
            Upload Prescription
          </h1>
          <p style={{ color: '#6B7280' }}>
            Submit your prescription for pharmacist review
          </p>
        </div>
        
        <Card>
          <div style={{
            background: '#EFF6FF',
            padding: 16,
            borderRadius: 8,
            marginBottom: 32,
            display: 'flex',
            gap: 12,
            alignItems: 'start'
          }}>
            <Icon name="alert" size={20} color="#3B82F6" />
            <div style={{ flex: 1, fontSize: 14, color: '#1E40AF' }}>
              <strong>What you need:</strong> A valid prescription from your doctor. 
              You can upload a photo, scan, or PDF. Our pharmacist will verify it within 24 hours.
            </div>
          </div>
          
          <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 16, color: brand.c.navy }}>
            Prescription Details
          </h3>
          
          <Input
            label="Medication Name"
            value={formData.medication}
            onChange={e => updateField('medication', e.target.value)}
            placeholder="e.g., Lisinopril 10mg"
            required
          />
          
          <Input
            label="Prescriber Name"
            value={formData.prescriber}
            onChange={e => updateField('prescriber', e.target.value)}
            placeholder="e.g., Dr. Sarah Johnson"
            required
          />
          
          <Input
            label="Prescriber Phone"
            type="tel"
            value={formData.prescriberPhone}
            onChange={e => updateField('prescriberPhone', e.target.value)}
            placeholder="(555) 123-4567"
            help="We may need to contact your prescriber to verify"
            required
          />
          
          <Input
            label="Quantity"
            value={formData.quantity}
            onChange={e => updateField('quantity', e.target.value)}
            placeholder="e.g., 90 tablets"
          />
          
          <Input
            label="Additional Notes (Optional)"
            type="textarea"
            value={formData.notes}
            onChange={e => updateField('notes', e.target.value)}
            placeholder="Any special instructions or information for the pharmacist"
            rows={3}
          />
          
          <div style={{ marginTop: 32, marginBottom: 32 }}>
            <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 16, color: brand.c.navy }}>
              Upload Prescription Image
            </h3>
            
            <div style={{
              border: '2px dashed #D1D5DB',
              borderRadius: 12,
              padding: 32,
              textAlign: 'center',
              background: '#F9FAFB',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onDragOver={(e) => {
              e.preventDefault();
              e.currentTarget.style.borderColor = brand.c.accent;
              e.currentTarget.style.background = '#EFF6FF';
            }}
            onDragLeave={(e) => {
              e.currentTarget.style.borderColor = '#D1D5DB';
              e.currentTarget.style.background = '#F9FAFB';
            }}
            onDrop={(e) => {
              e.preventDefault();
              e.currentTarget.style.borderColor = '#D1D5DB';
              e.currentTarget.style.background = '#F9FAFB';
              const droppedFile = e.dataTransfer.files[0];
              if (droppedFile) {
                setFile(droppedFile);
                if (droppedFile.type.startsWith('image/')) {
                  const reader = new FileReader();
                  reader.onloadend = () => setFilePreview(reader.result);
                  reader.readAsDataURL(droppedFile);
                }
              }
            }}
            onClick={() => document.getElementById('file-upload').click()}>
              {filePreview ? (
                <div>
                  <img src={filePreview} alt="Preview" style={{ maxWidth: '100%', maxHeight: 300, borderRadius: 8, marginBottom: 16 }} />
                  <p style={{ fontSize: 14, color: '#6B7280' }}>
                    {file?.name}
                  </p>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setFile(null);
                      setFilePreview(null);
                    }}
                    style={{ marginTop: 12 }}
                  >
                    Remove
                  </Button>
                </div>
              ) : (
                <>
                  <Icon name="upload" size={48} color="#9CA3AF" style={{ marginBottom: 16 }} />
                  <p style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>
                    Click to upload or drag and drop
                  </p>
                  <p style={{ fontSize: 14, color: '#6B7280' }}>
                    PNG, JPG, PDF up to 10MB
                  </p>
                </>
              )}
              <input
                id="file-upload"
                type="file"
                accept="image/*,.pdf"
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
            </div>
          </div>
          
          <div style={{
            background: '#FEF3C7',
            padding: 16,
            borderRadius: 8,
            marginBottom: 24,
            fontSize: 13,
            color: '#92400E'
          }}>
            <strong>Privacy Note:</strong> Your prescription information is protected under HIPAA and stored securely. 
            Only licensed pharmacists will review your submission.
          </div>
          
          <div style={{ display: 'flex', gap: 12 }}>
            <Button onClick={handleSubmit} icon="upload" fullWidth>
              Submit Prescription
            </Button>
            <Button variant="secondary" onClick={() => onNavigate('dashboard')}>
              Cancel
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
}
